import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BookOpen, CheckCircle, Lock, Trophy, TrendingUp, ArrowRight, AlertCircle } from 'lucide-react';
import api from '../services/api';

function LearningPath() {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchEnrollment = async () => {
      try {
        const response = await api.get(`/enrollments/${enrollmentId}`);
        setEnrollment(response.data);
      } catch (error) {
        console.error('Error fetching enrollment:', error);
        setError('Failed to load learning path');
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollment();
  }, [enrollmentId]);

  const handleStartAssessment = async (sectionOrder) => {
    try {
      const response = await api.get(
        `/assessments/program/${enrollment.program._id}/section/${sectionOrder}`
      );
      navigate(`/assessment/${response.data._id}?enrollmentId=${enrollmentId}&sectionOrder=${sectionOrder}&programId=${enrollment.program._id}`);
    } catch (error) {
      console.error('Error loading assessment:', error);
      alert('Failed to load assessment');
    }
  };

  const handleStartLearning = (sectionOrder) => {
    navigate(`/content/${enrollment.program._id}/${sectionOrder}?enrollmentId=${enrollmentId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading learning path...</p>
        </div>
      </div>
    );
  }

  if (!enrollment) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-700">Enrollment not found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div style={{marginTop: "73px"}} className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {enrollment.program?.title}
          </h1>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-200 font-medium">Overall Progress</span>
              <span className="text-2xl font-bold">{enrollment.overallProgress}%</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-4 overflow-hidden">
              <div
                className="bg-gradient-to-r from-green-400 to-green-500 h-full rounded-full transition-all duration-500"
                style={{ width: `${enrollment.overallProgress}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <h2 className="text-3xl font-bold text-slate-900 mb-8 flex items-center gap-3">
          <BookOpen className="w-8 h-8" />
          Learning Sections
        </h2>

        <div className="space-y-6">
          {enrollment.sectionsProgress?.map((section, index) => {
            const isCompleted = section.status === 'completed';
            const isLocked = section.status === 'locked';
            const isInProgress = section.status === 'in-progress';

            return (
              <div
                key={section._id}
                className={`relative border-2 rounded-xl shadow-sm hover:shadow-lg transition-all ${
                  isCompleted
                    ? 'border-green-200 bg-green-50'
                    : isLocked
                    ? 'border-slate-200 bg-slate-50 opacity-60'
                    : 'border-blue-200 bg-blue-50'
                }`}
              >
                {/* Section Number Badge */}
                <div
                  className={`absolute -left-4 -top-4 w-12 h-12 rounded-full flex items-center justify-center font-bold text-white shadow-lg ${
                    isCompleted
                      ? 'bg-green-500'
                      : isLocked
                      ? 'bg-slate-400'
                      : 'bg-blue-500'
                  }`}
                >
                  {section.sectionOrder}
                </div>

                <div className="p-6 pl-12">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    {/* Section Info */}
                    <div className="flex-1">
                      <div className="flex items-start gap-3 mb-2">
                        {isCompleted && <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />}
                        {isLocked && <Lock className="w-6 h-6 text-slate-400 flex-shrink-0 mt-1" />}
                        {isInProgress && <TrendingUp className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />}
                        <div>
                          <h3 className="text-xl font-bold text-slate-900">
                            Section {section.sectionOrder}: {section.sectionName}
                          </h3>
                          {isCompleted && (
                            <div className="flex items-center gap-2 mt-2">
                              <Trophy className="w-4 h-4 text-green-600" />
                              <span className="text-green-700 font-medium">
                                Score: {section.assessmentScore}%
                              </span>
                            </div>
                          )}
                          {isInProgress && (
                            <p className="text-blue-700 text-sm mt-1 font-medium">
                              Ready to start learning
                            </p>
                          )}
                          {isLocked && (
                            <p className="text-slate-500 text-sm mt-1">
                              Complete previous sections to unlock
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3">
                      {isInProgress && (
                        <>
                          <button
                            onClick={() => handleStartLearning(section.sectionOrder)}
                            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all shadow-md hover:shadow-lg"
                          >
                            <BookOpen className="w-5 h-5" />
                            Start Learning
                            <ArrowRight className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleStartAssessment(section.sectionOrder)}
                            className="flex items-center gap-2 px-6 py-3 bg-white text-blue-600 border-2 border-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-all"
                          >
                            <Trophy className="w-5 h-5" />
                            Take Assessment
                          </button>
                        </>
                      )}
                      {isCompleted && (
                        <button
                          disabled
                          className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold cursor-not-allowed opacity-75"
                        >
                          <CheckCircle className="w-5 h-5" />
                          Completed
                        </button>
                      )}
                      {isLocked && (
                        <button
                          disabled
                          className="flex items-center gap-2 px-6 py-3 bg-slate-300 text-slate-600 rounded-lg font-semibold cursor-not-allowed"
                        >
                          <Lock className="w-5 h-5" />
                          Locked
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {enrollment.enrollmentStatus === 'completed' && (
          <div className="mt-12 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl p-8 text-center shadow-xl">
            <Trophy className="w-16 h-16 mx-auto mb-4" />
            <h3 className="text-3xl font-bold mb-2">Congratulations! 🎉</h3>
            <p className="text-green-100 text-lg mb-6">
              You've completed all sections of this program!
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-8 py-3 bg-white text-green-600 rounded-lg font-semibold hover:bg-green-50 transition-all shadow-lg hover:shadow-xl"
            >
              Back to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default LearningPath;
